import { AiImportResponseTypes, ChunkMessage } from "@/constants/enums";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { CONTENT_TO_STEP_INDEX, isFinalContent, parseKarlChunk } from "@/utils/aiImport";
import { useEffect } from "react";
import { useAIImportStatusPolling } from "./queries";
import { useSSE } from "./useSSE";

/**
 * Hook that wires up SSE consumption for a specific AI Import ID and applies
 * side-effects to the runtime store based on incoming events.
 *
 * getLocalizedStepContent is injected to avoid coupling this hook to i18n.
 */
export function useActiveImportStream(
  aiImportId: number,
  getLocalizedStepContent: (_stepIndex: number, _messageType: ChunkMessage) => string
) {
  const runtime = useAiImportRuntime((s) => s.activeImports[aiImportId]);
  const setLastEventId = useAiImportRuntime((s) => s.setLastEventId);
  const appendStep = useAiImportRuntime((s) => s.appendStep);
  const endImport = useAiImportRuntime((s) => s.endImport);
  const { setIsPolling } = useAIImportStatusPolling();
  const { status, lastMessage } = useSSE({ enabled: true, aiImportId, lastEventId: runtime?.lastEventId ?? undefined });

  useEffect(() => {
    if (!lastMessage) return;
    const events = parseKarlChunk(lastMessage);
    if (!events.length) return;

    for (const ev of events) {
      if (ev?.karl_event !== "custom") continue;
      if (ev?.type && ev.type !== AiImportResponseTypes.PROGRESS) continue;

      const evImportId: string | undefined = ev?.ai_import_id;
      if (!evImportId || String(evImportId) !== String(aiImportId)) continue;

      const content: string | undefined = ev?.content;
      if (!content) continue;

      const stepIndex = CONTENT_TO_STEP_INDEX[content];
      if (typeof stepIndex !== "number") continue;

      setLastEventId(aiImportId, ev.id);

      appendStep(aiImportId, {
        id: stepIndex,
        node: ev?.node ?? "Node",
        type: AiImportResponseTypes.PROGRESS,
        title: getLocalizedStepContent(stepIndex, ChunkMessage.TITLE),
        content: getLocalizedStepContent(stepIndex, ChunkMessage.START_MESSAGE),
      });

      const isFinal = isFinalContent(content);
      if (isFinal) {
        setTimeout(() => {
          setIsPolling(true);
          setTimeout(() => {
            endImport(aiImportId);
          }, 2000);
        }, 1200);
      }
    }

  }, [lastMessage, aiImportId, appendStep, endImport, setIsPolling, setLastEventId]);

  return { status };
}
